import { auditAuditLog, useAuditAuditFacets, useAuditOperations } from "@api/generated/audit/audit";
import type { AuditRow, OperationRow, QualityCheck } from "@api/generated/model";
import { useWorkspaceLegacy } from "@api/generated/workspace/workspace";
import { QUALITY_LINKS, QualityRow, qualityTone, useQuality } from "@features/system/quality";
import { UndoDialog } from "@features/system/undo";
import { Button, Loader, Select, Tabs, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AnimatedTabsList } from "@ui/AnimatedTabsList";
import { changeText, recordKeyText, useTableText } from "@ui/audit";
import { formatNumber, fullTime, shortTime } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { Splitter } from "@ui/Splitter";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const TABS = ["changes", "operations", "quality", "legacy"] as const;
const search = z.object({
  tab: z.enum(TABS).optional(),
  user: z.string().optional(),
  table: z.string().optional(),
  source: z.string().optional(),
  period: z.enum(["today", "last7Days"]).optional(),
  request_id: z.string().optional(),
});
export const Route = createFileRoute("/system/journal")({ validateSearch: search, component: AuditPage });

function AuditPage() {
  const { t } = useTranslation();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const quality = useQuality();
  return (
    <div className="page fill">
      <PageHeader title={t("journal.title")} />
      <Tabs
        value={params.tab ?? "changes"}
        onChange={(v) => v && navigate({ search: { tab: v as (typeof TABS)[number] } })}
        color="breeder"
        keepMounted={false}
        style={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 0 }}
      >
        <AnimatedTabsList mb="md">
          <Tabs.Tab value="changes">{t("journal.tab.changes")}</Tabs.Tab>
          <Tabs.Tab value="operations" rightSection={<span className="mu sm">{t("journal.undoable")}</span>}>
            {t("journal.tab.operations")}
          </Tabs.Tab>
          <Tabs.Tab
            value="quality"
            rightSection={quality.data && <span className="mu sm">{t("dataQuality.checkCount", { count: quality.data.length })}</span>}
          >
            {t("journal.tab.dataQuality")}
          </Tabs.Tab>
          <Tabs.Tab value="legacy">{t("journal.tab.legacy")}</Tabs.Tab>
        </AnimatedTabsList>
        <Tabs.Panel value="changes" className="fill-row">
          <ChangeLog />
          <Splitter cssVar="--quality-aside-w" side="end" min={240} max={640} />
          <QualityAside />
        </Tabs.Panel>
        <Tabs.Panel value="operations" className="fill-row">
          <Operations />
        </Tabs.Panel>
        <Tabs.Panel value="quality" className="fill-row">
          <QualityTable />
        </Tabs.Panel>
        <Tabs.Panel value="legacy">
          <Legacy />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}

function since(period: "today" | "last7Days" | undefined): string | undefined {
  if (!period) return undefined;
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (period === "last7Days") d.setDate(d.getDate() - 6);
  return d.toISOString();
}

function ChangeLog() {
  const { t } = useTranslation();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const facets = useAuditAuditFacets({ query: { staleTime: 10 * 60_000 } });
  const tableText = useTableText();
  const [text, setText] = useState("");
  const [q] = useDebouncedValue(text.trim(), 300);
  const filter = {
    username: params.user,
    table: params.table,
    source: params.source,
    since: since(params.period),
    request_id: params.request_id,
    q: q || undefined,
  };
  const log = useInfiniteQuery({
    queryKey: ["audit", filter],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => auditAuditLog({ ...filter, cursor: pageParam, limit: 100 }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const rows = useMemo(() => log.data?.pages.flatMap((p) => p.rows) ?? [], [log.data]);
  const columns = useMemo<Column<AuditRow>[]>(
    () => [
      {
        id: "ts",
        header: t("journal.time"),
        value: (r) => r.ts,
        text: (r) => shortTime(r.ts, true),
        cell: (r) => <span title={fullTime(r.ts)}>{shortTime(r.ts, true)}</span>,
        className: "mono",
      },
      { id: "user", header: t("journal.user"), value: (r) => r.username },
      {
        id: "source",
        header: t("journal.source"),
        value: (r) => t(`journal.sources.${r.source}`, { defaultValue: r.source }),
        cell: (r) => (
          <span className={`chip ${r.source === "device" ? "b" : r.source === "cli" ? "y" : ""}`}>
            {t(`journal.sources.${r.source}`, { defaultValue: r.source })}
          </span>
        ),
      },
      {
        id: "table",
        header: t("journal.table"),
        value: (r) => tableText(r.table),
        cellProps: (r) => ({ title: r.table }),
      },
      {
        id: "record",
        header: t("journal.record"),
        value: (r) => r.record ?? recordKeyText(r.pk as Record<string, unknown>),
        className: "mono",
      },
      { id: "change", header: t("journal.change"), value: (r) => changeText(r, t), className: "mono wrap" },
    ],
    [t, tableText],
  );
  const table = useDataTable("journal.changes", columns, rows, { partial: !!log.hasNextPage });
  const set = (patch: Record<string, string | undefined>) => navigate({ search: (s) => ({ ...s, ...patch }) });
  const facet = (key: "user" | "table" | "source", label: string, data: { value: string; label: string }[], w: number) => (
    <Select
      size="xs"
      w={w}
      placeholder={`${label}: ${t("journal.all")}`}
      data={data}
      value={params[key] ?? null}
      onChange={(v) => set({ [key]: v ?? undefined })}
      clearable
      searchable
      aria-label={label}
    />
  );
  const plain = (xs: string[] | undefined) => (xs ?? []).map((x) => ({ value: x, label: x }));

  return (
    <section className="card grow" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div
        className="hstack"
        style={{ gap: 8, padding: "var(--b-row-padding)", borderBottom: "1px solid var(--b-border)", flexWrap: "wrap" }}
      >
        {facet("user", t("journal.user"), plain(facets.data?.users), 170)}
        {facet(
          "table",
          t("journal.table"),
          (facets.data?.tables ?? []).map((x) => ({ value: x, label: tableText(x) })),
          160,
        )}
        {facet(
          "source",
          t("journal.source"),
          (facets.data?.sources ?? []).map((v) => ({ value: v, label: t(`journal.sources.${v}`, { defaultValue: v }) })),
          150,
        )}
        {(["today", "last7Days"] as const).map((p) => (
          <button
            key={p}
            type="button"
            className={`chip ${params.period === p ? "g" : ""}`}
            onClick={() => set({ period: params.period === p ? undefined : p })}
          >
            {t(`journal.${p}`)}
            {params.period === p && " ✕"}
          </button>
        ))}
        {params.request_id && (
          <button type="button" className="chip g" onClick={() => set({ request_id: undefined })}>
            {t("journal.operation")} ✕
          </button>
        )}
        <div style={{ flexGrow: 1 }} />
        <TextInput
          size="xs"
          w={200}
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
          placeholder={t("journal.searchPlaceholder")}
          aria-label={t("journal.searchPlaceholder")}
        />
      </div>
      <DataTable table={table} rowKey={(r) => r.id} className="grow" />
      {log.isPending && <Loader size="sm" m="md" />}
      {!log.isPending && rows.length === 0 && (
        <p className="sm mu" style={{ padding: 14 }}>
          {t("journal.empty")}
        </p>
      )}
      {log.hasNextPage && (
        <Button variant="default" m="sm" loading={log.isFetchingNextPage} onClick={() => void log.fetchNextPage()}>
          {t("journal.older")}
        </Button>
      )}
      <div className="sm mu" style={{ padding: "var(--b-row-padding)", borderTop: "1px solid var(--b-border)" }}>
        {t("journal.appendOnly")}
      </div>
    </section>
  );
}

function QualityAside() {
  const { t } = useTranslation();
  const quality = useQuality();
  const legacy = useWorkspaceLegacy({ query: { staleTime: 10 * 60_000 } });
  return (
    <aside className="card pad" style={{ width: "var(--quality-aside-w, 320px)", flexShrink: 0, gap: 8, overflowY: "auto" }}>
      <div className="hstack">
        <h2 className="h2 grow">{t("journal.tab.dataQuality")}</h2>
        <span className="sm mu">{t("dataQuality.liveCheck")}</span>
      </div>
      {(quality.data ?? [])
        .filter((c) => c.count > 0)
        .map((c) => (
          <QualityRow key={c.key} check={c} />
        ))}
      {(quality.data ?? []).some((c) => c.count === 0) && (
        <span className="sm mu">{t("journal.moreChecksOk", { count: (quality.data ?? []).filter((c) => c.count === 0).length })}</span>
      )}
      <div style={{ flexGrow: 1 }} />
      {legacy.data && (
        <div className="note">
          <b>{t("journal.tab.legacy")}:</b> {t("journal.fileCount", { count: legacy.data.files })},{" "}
          {t("journal.rowsImported", { count: formatNumber(legacy.data.rows) })} ·{" "}
          {t(`journal.legacyStatus.${legacy.data.status ?? "null"}`)}
        </div>
      )}
    </aside>
  );
}

function Operations() {
  const { t } = useTranslation();
  const ops = useAuditOperations();
  const [undoing, setUndoing] = useState<OperationRow | null>(null);
  const columns = useMemo<Column<OperationRow>[]>(
    () => [
      {
        id: "time",
        header: t("journal.time"),
        value: (op) => op.created_at,
        text: (op) => shortTime(op.created_at),
        className: "mono",
      },
      { id: "user", header: t("journal.user"), value: (op) => op.username },
      { id: "operation", header: t("journal.operation"), value: (op) => op.title, className: "wrap" },
      {
        id: "changes",
        header: t("journal.change"),
        value: (op) => op.changes ?? 0,
        numeric: true,
        cell: (op) => (
          <Link to="/system/journal" search={{ tab: "changes", request_id: op.request_id }}>
            {t("journal.changeCount", { count: formatNumber(op.changes ?? 0) })}
          </Link>
        ),
      },
      {
        id: "undo",
        header: "",
        name: t("journal.undo"),
        fixed: true,
        cell: (op) =>
          op.undone_at ? (
            <span className="chip">{t("journal.undoneBy", { user: op.undone_by })}</span>
          ) : (
            <Button size="compact-xs" variant="default" onClick={() => setUndoing(op)}>
              {t("journal.undo")}
            </Button>
          ),
      },
    ],
    [t],
  );
  const table = useDataTable("journal.operations", columns, ops.data);
  return (
    <section className="card grow stack" style={{ minHeight: 0 }}>
      <p className="sm mu" style={{ padding: "12px 14px 0", margin: 0 }}>
        {t("journal.operationsDescription")}
      </p>
      {ops.isPending && <Loader size="sm" m="md" />}
      {ops.data?.length === 0 && (
        <p className="sm mu" style={{ padding: 14 }}>
          {t("journal.noOperations")}
        </p>
      )}
      {!!ops.data?.length && (
        <DataTable table={table} rowKey={(op) => op.id} rowProps={(op) => (op.undone_at ? { style: { opacity: 0.6 } } : {})} />
      )}
      {undoing && <UndoDialog opId={undoing.id} title={undoing.title} onClose={() => setUndoing(null)} />}
    </section>
  );
}

function QualityTable() {
  const { t } = useTranslation();
  const quality = useQuality();
  const columns = useMemo<Column<QualityCheck>[]>(
    () => [
      {
        id: "check",
        header: t("journal.check"),
        value: (c) => t(`dataQuality.${c.key}`),
        className: "wrap",
        cell: (c) => (
          <>
            <span className={`chip ${qualityTone(c).chip}`} style={{ marginRight: 8 }}>
              {c.scope_year ?? "∑"}
            </span>
            {t(`dataQuality.${c.key}`)}
          </>
        ),
      },
      { id: "count", header: t("journal.count"), value: (c) => c.count, numeric: true, className: "mono" },
      {
        id: "resolution",
        header: t("journal.resolution"),
        cell: (c) => {
          const to = QUALITY_LINKS[c.key];
          return to && c.count > 0 ? <Link to={to}>{t("journal.open")}</Link> : null;
        },
      },
    ],
    [t],
  );
  const table = useDataTable("journal.quality", columns, quality.data);
  return (
    <section className="card grow stack" style={{ minHeight: 0 }}>
      <DataTable table={table} rowKey={(c) => c.key} className="grow" />
    </section>
  );
}

function Legacy() {
  const { t } = useTranslation();
  const l = useWorkspaceLegacy({ query: { staleTime: 10 * 60_000 } }).data;
  return (
    <section className="card pad" style={{ maxWidth: 640 }}>
      <span className="sm mu">{t("journal.legacyDescription")}</span>
      {l && (
        <>
          <div className="hstack" style={{ gap: 8 }}>
            <span className={`chip ${l.status === "locked" ? "g" : l.status === "failed" ? "r" : "y"}`}>
              {t(`journal.legacyStatus.${l.status ?? "null"}`)}
            </span>
            <span>{t("journal.fileCount", { count: l.files })}</span>
            <span>·</span>
            <span>{t("journal.rowsImported", { count: formatNumber(l.rows) })}</span>
          </div>
          <span className="sm mu">
            {fullTime(l.started_at)} – {fullTime(l.finished_at)}
            {l.locked_at && ` · ${fullTime(l.locked_at)}`}
          </span>
        </>
      )}
    </section>
  );
}
